<script lang="ts">
  import type { DistributorSellSheet } from "@shared/types/SellSheet";
  import * as _ from "lodash";
  import PageWrapper from "@retailer/svelte/Shared/PageWrapper.svelte";
  import ShareSellSheetModal from "@distributor/svelte/SellSheet/Components/ShareSellSheetModal.svelte";
  import CopySellSheetModal from "@distributor/svelte/SellSheet/Components/CopySellSheetModal.svelte";
  import ChooseDistributorModal from "@distributor/svelte/SellSheet/Components/ChooseDistributorModal.svelte";
  import LoadingSpinner from "@shared/svelte/Components/LoadingSpinner.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import TableSortControl from "@shared/svelte/Components/TableSortControl.svelte";
  import { goto } from "@roxi/routify";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import Pagination from "@shared/svelte/Components/Pagination/Small.svelte";
  import dateHelpers from "@shared/filters/tiz_date.js";
  import { userNameStringUtil } from "@shared/services/user.js";
  import Popover from "@shared/svelte/Components/Popover/Popover.svelte";
  import Arrow from "@shared/svelte/Components/Arrow.svelte";

  export let sellSheetsRes: any;
  export let order: string;
  export let direction: string;
  export let search: string;
  export let page: string;

  if (
    !_.get(window["$rootScope"], "user.distributors") ||
    !_.some(window["$rootScope"].user.distributors, function (x) {
      return x.uses_sell_sheets;
    })
  ) {
    window["stateService"].go("layout.home");
  }

  let sellSheets: DistributorSellSheet[] = sellSheetsRes;
  let loadingSearchResults = false;
  let showShareSellSheetModal = false;
  let retailerUserCount = 0;
  let sheetToShare: DistributorSellSheet = null;
  let sheetToCopy: DistributorSellSheet = null;
  let showCopyModal = false;
  let retailersLoaded = false;
  let showChooseDistributorModal = false;
  let distributors = window["$rootScope"].user.distributors;
  let selectedDistributor = null;
  let status = { loading: false, errors: null };
  let multipleDistroUser = window["$rootScope"].user.distributors.length > 1 ? true : false;
  let tableSorters: object;

  const refreshPage = function (changes) {
    window["stateService"].go(
      "layout.product_sheets",
      _.merge(
        {
          order: order,
          direction: direction,
          page: page,
          search: search,
        },
        changes || {},
      ),
    );
  };

  let pagination = {
    perPage: parseInt(sellSheetsRes.headers("X-Pagination-Limit")),
    page: parseInt(page) || 1,
    totalEntries: parseInt(sellSheetsRes.headers("X-Pagination-Entries")),
    goToPage: function (n) {
      page = n;
      refreshPage({});
    },
  };

  const searchSellSheets = async function () {
    loadingSearchResults = true;
    var attrs = {
      order: order,
      direction: direction,
      page: page,
      search: search,
    };
    try {
      let res = await HttpPromise({
        method: "GET",
        url: "/api/distributor/sell_sheets",
        data: attrs,
      });
      sellSheets = res;
      loadingSearchResults = false;
    } catch (error) {
      status.errors = error;
      loadingSearchResults = false;
    }
  };

  const updateSearch = function () {
    loadingSearchResults = true;
    page = "1";
    searchSellSheets();
  };
  const debouncedUpdateSearch = _.debounce(updateSearch, 250);

  const createSellSheet = async function (distributorId) {
    try {
      let res = await HttpPromise({
        method: "POST",
        url: "/api/distributor/sell_sheets",
        data: {
          name: "Untitled Sheet",
          distributor_id: distributorId,
        },
      });
      if (window["C"].APP_NAME === "supplier") {
        $goto("/supplier/product_sheets/:productSheetId", { productSheetId: res.id });
      } else {
        $goto("/distributor/product_sheets/:productSheetId", { productSheetId: res.id });
      }
    } catch (error) {
      status.errors = error;
    }
  };

  const share = function (sellSheet) {
    sheetToShare = sellSheet;
    retailerUserCount = sellSheets.find(sheet => sheet.id === sellSheet.id).retailer_count || 0;
    showShareSellSheetModal = true;
  };

  const onCloseModal = function () {
    showShareSellSheetModal = false;
    showCopyModal = false;
    sheetToCopy = null;
  };

  const duplicate = function (sellSheet) {
    sheetToCopy = sellSheet;
    showCopyModal = true;
  };

  const deleteSheet = async function (sellSheet) {
    if (status.loading) {
      return;
    }
    status.loading = true;
    try {
      await HttpPromise({
        method: "DELETE",
        url: "/api/distributor/sell_sheets/" + sellSheet.id,
      });
      Flash.now({ message: "Product Sheet Deleted", type: "success" });
      window["stateService"].reload();
    } catch (error) {
      status.errors = error;
      status.loading = false;
    }
  };

  const createdByUser = function (sellSheet) {
    if (window["$rootScope"].user.id == sellSheet.created_by_id) {
      return "You";
    }
    return userNameStringUtil(sellSheet.created_by_user);
  };

  const shareWithAll = async function () {
    onCloseModal();
    status.loading = true;
    try {
      await HttpPromise({
        method: "POST",
        url: "/api/distributor/sell_sheets/share_with_all_retailers",
        data: {
          sell_sheet_id: sheetToShare.id,
        },
      });
      let flashMethod = window["modern_mode"] ? Flash.now : Flash.queue;
      flashMethod({
        message: "Your product sheet has been successfully shared with selected retailers",
        type: "success",
      });
      window["stateService"].reload();
    } catch (error) {
      status.errors = error;
      status.loading = false;
    }
  };

  const shareWithSelected = async function (retailerIds) {
    onCloseModal();
    status.loading = true;
    try {
      await HttpPromise({
        method: "POST",
        url: "/api/distributor/sell_sheets/share_with_selected_retailers",
        data: {
          sell_sheet_id: sheetToShare.id,
          retailer_users: retailerIds,
        },
      });
      let flashMethod = window["modern_mode"] ? Flash.now : Flash.queue;
      flashMethod({
        message: "Your product sheet has been successfully shared with selected retailers",
        type: "success",
      });
      window["stateService"].reload();
    } catch (error) {
      status.errors = error;
      status.loading = false;
    }
  };

  const searchRetailers = async function (distributorId, searchTerm) {
    try {
      let res = await HttpPromise({
        method: "GET",
        url: `/api/distributor/sell_sheets/${sheetToShare.id}/retailers`,
        data: {
          search_term: searchTerm,
          distributor_id: distributorId,
        },
      });
      retailersLoaded = true;
      status.loading = false;
      return res;
    } catch (error) {
      status.errors = error;
      status.loading = false;
    }
  };

  const chooseDistributor = function () {
    if (distributors.length > 1) {
      showChooseDistributorModal = true;
    } else {
      createSellSheet(distributors[0].id);
    }
  };

  const closeChooseDistributorModal = function () {
    showChooseDistributorModal = false;
  };
  const selectDistributor = function (distributorName) {
    closeChooseDistributorModal();
    status.loading = true;
    let distributor = distributors.find(d => d.name === distributorName);
    createSellSheet(distributor.distributor_id);
  };

  $: {
    tableSorters = {
      type: order || "",
      direction: direction || "",
      sortCallBack: params => {
        order = params.order;
        direction = params.direction;
        searchSellSheets();
      },
    };
  }
</script>

<PageWrapper let:mobileMode useBackgroundWhite={false}>
  <div class="distributor-page">
    <div class="background-white">
      <div class="grid-container">
        <div class="grid-row align--middle padding-top--smaller padding-bottom--smaller">
          <div class="grid-col c12-12">
            <span class="default-subheader--bold">Product Sheets</span>
          </div>
        </div>
      </div>
    </div>
    <FormErrors errors={status.errors} />
    <div class="grid-container margin-top--smaller">
      <div class="grid-row">
        <div class="grid-col c12-12 align-left background-white">
          <div class="grid-row padding-top--smaller padding-bottom--smaller">
            <div class="grid-col c12-12 vertical-align-middle">
              <div class="media">
                <div class="input-wrapper full-width media-fluid">
                  <input
                    type="text"
                    class="input"
                    bind:value={search}
                    data-testid="search-input"
                    on:keyup={debouncedUpdateSearch}
                    placeholder="Search Product Sheets..."
                  />
                </div>

                <div class="grid-col c6-12 align-right">
                  <button class="button button--primary" on:click={chooseDistributor}
                    >Create Product Sheet</button
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- DESKTOP STYLES -->
    <div class="grid-container margin-top--smaller" class:display-none={mobileMode}>
      <div class="grid-row padding--smaller background-white">
        <div class="grid-row padding-bottom--smaller">
          <div class="grid-col" class:c3-12={multipleDistroUser} class:c5-12={!multipleDistroUser}>
            <div class="default-body--bold color-black padding-left--smaller name-sort">
              Name <TableSortControl
                sortFilter={tableSorters}
                colName={"name"}
                displayInline={"display-inline-block"}
              />
            </div>
          </div>
          {#if multipleDistroUser}
            <div class="grid-col c2-12">
              <div class="default-body--bold color-black"> Distributor </div>
            </div>
          {/if}
          <div class="grid-col c1-12">
            <div class="default-body--bold color-black">
              Retailers <TableSortControl
                sortFilter={tableSorters}
                colName={"accounts"}
                displayInline={"display-inline-block"}
              />
            </div>
          </div>
          <div class="grid-col c1-12">
            <div class="default-body--bold color-black">
              Total Views <TableSortControl
                sortFilter={tableSorters}
                colName={"views"}
                displayInline={"display-inline-block"}
              />
            </div>
          </div>
          <div class="grid-col c1-12">
            <div class="default-body--bold color-black">
              Products Sold <TableSortControl
                sortFilter={tableSorters}
                colName={"products_sold"}
                displayInline={"display-inline-block"}
              />
            </div>
          </div>
          <div class="grid-col c2-12">
            <div class="default-body--bold color-black">
              Created Date <TableSortControl
                sortFilter={tableSorters}
                colName={"created_at"}
                displayInline={"display-inline-block"}
              />
            </div>
          </div>
          <div class="grid-col c2-12"> </div>
        </div>
        {#if loadingSearchResults || status.loading}
          <div class="tile fizz-styles">
            <LoadingSpinner />
          </div>
        {:else}
          <div class="tile">
            {#each sellSheets as sellSheet}
              <div class="tile__row padding--smaller sell-sheet-row" id="sell-sheet-{sellSheet.id}">
                <div class="grid-row">
                  <div class="grid-col {multipleDistroUser ? 'c3-12' : 'c5-12'}">
                    <UIRouterLink
                      stateName={"layout.product_sheet"}
                      params={{ productSheetId: sellSheet.id }}
                    >
                      <span
                        class="default-body--bold default-subheader--bold--sm show-link cursor-pointer underline-hover color-brandPrimary"
                      >
                        {sellSheet.name}
                      </span>
                    </UIRouterLink>
                  </div>
                  {#if multipleDistroUser}
                    <div class="grid-col c2-12" style="padding-left: 4px;">
                      <span class="default-body--regular">{sellSheet.distributor_name}</span>
                    </div>
                  {/if}
                  <div class="grid-col c1-12">
                    <span class="default-body--regular">{sellSheet.shared_retailer_count}</span>
                  </div>
                  <div class="grid-col c1-12">
                    <span class="default-body--regular">{sellSheet.total_views}</span>
                  </div>
                  <div class="grid-col c1-12">
                    <span class="default-body--regular"
                      >{sellSheet.num_unique_retailer_pl_purchases}</span
                    >
                  </div>
                  <div class="grid-col c2-12">
                    <span class="default-body--regular float-left"
                      >{dateHelpers.dateNoTime()(sellSheet.created_at)}</span
                    >
                    <span
                      class="default-body--regular ellipsis color-gray float-left padding-left--smallest"
                      >{createdByUser(sellSheet)}</span
                    >
                  </div>
                  <div class="grid-col c2-12">
                    <span class="float-right position-relative">
                      <Popover arrow={false} popoverElement={"span"} placement={"bottom-center"}>
                        <a
                          class="button button--secondary button--smallest settings-link"
                          href={"#"}
                          on:click|preventDefault
                          slot="target"
                          >More <Arrow direction="down" />
                        </a>

                        <ul class="list color-black sell-sheet-popover" slot="content" let:setOpen>
                          <li class="list-item cursor-pointer padding--smaller background-hover">
                            <UIRouterLink
                              stateName={"layout.product_sheet_preview"}
                              params={{ productSheetId: sellSheet.id, pdf: true }}
                              classes={"download-link"}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Download
                            </UIRouterLink>
                          </li>
                          <li
                            class="list-item cursor-pointer padding--smaller background-hover duplicate-link"
                            on:click={() => {
                              setOpen();
                              duplicate(sellSheet);
                            }}
                          >
                            <span>Share with Reps</span>
                          </li>
                          <li
                            class="list-item cursor-pointer padding--smaller background-hover delete-link"
                            on:click={() => {
                              setOpen();
                              deleteSheet(sellSheet);
                            }}
                          >
                            <span class="color-red">Delete</span>
                          </li>
                        </ul>
                      </Popover>
                      <a
                        class="button button--primary button--smallest share-link"
                        href={"#"}
                        on:click|preventDefault={() => share(sellSheet)}>Share</a
                      >
                    </span>
                  </div>
                </div>
              </div>
            {/each}
            {#if !sellSheets.length && !search}
              <div>
                <div class="grid-row align-center">
                  <div class="grid-col c12-12 padding--3">
                    <div style="height: 90px;"
                      ><Icon type="SellSheets" width="80" height="80" /></div
                    >
                    <div class="s3 color-grayDark margin-top--medium padding-bottom--smaller"
                      >Create a Product Sheet to get started!</div
                    >
                    <div class="s4 color-grayDark"
                      >Choose products from your inventory and share them with your retailers.</div
                    >
                  </div>
                </div>
              </div>
            {/if}
            {#if !sellSheets.length && search}
              <div>
                <div class="grid-row padding-top--smaller">
                  <div class="grid-col c12-12 align-center">
                    <div class="s3 color-grayDark padding-bottom--medium padding-top--small"
                      >No Product Sheets found for this search.</div
                    >
                  </div>
                </div>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>
    <!-- MOBILE STYLES -->
    <div class="grid-container" class:display-none={!mobileMode}>
      <div class="grid-row padding-left--smaller padding-right--smaller padding-bottom--smaller">
        {#each sellSheets as sellSheet}
          <div id="sell-sheet-{sellSheet.id}">
            <div class="grid-row padding--smaller background-white margin-top--smaller">
              <div class="grid-col c8-12">
                <UIRouterLink
                  stateName={"layout.product_sheet"}
                  params={{ productSheetId: sellSheet.id }}
                >
                  <span class="default-subheader--bold cursor-pointer color-brandPrimary show-link">
                    {sellSheet.name}
                  </span>
                </UIRouterLink>
              </div>
              <div class="grid-col c4-12 align-right">
                <span class="default-small--regular default-body--regular--sm vertical-align-middle"
                  >{dateHelpers.dateNoTime()(sellSheet.created_at)}</span
                >
              </div>
            </div>
            <div class="grid-row padding--smaller background-white" style="margin-top: 2px;">
              <div class="grid-col c12-12">
                <span class="default-small--regular default-body--regular--sm vertical-align-middle"
                  >{sellSheet.number_of_product_lines} Products, {sellSheet.number_of_shares} Recipients</span
                >
                <span class="float-right">
                  <Popover popoverElement={"span"} arrow={false}>
                    <a
                      class="button button--secondary button--smallest settings-link"
                      href={"#"}
                      slot="target">More <Arrow direction="up" /></a
                    >
                    <!-- <span class='position-relative' id='settingsPopover' style="right: 38px;"> -->

                    <ul class="list color-black" slot="content">
                      <li
                        class="list-item cursor-pointer padding--smaller background-hover duplicate-link"
                        on:click={() => duplicate(sellSheet)}
                      >
                        <span>Share with reps</span>
                      </li>
                      <li
                        class="list-item cursor-pointer padding--smaller background-hover delete-link"
                        on:click={() => deleteSheet(sellSheet)}
                      >
                        <span class="color-red">Delete</span>
                      </li>
                    </ul>
                  </Popover>
                  <!-- </span> -->
                  <a
                    class="button button--primary button--smallest share-link"
                    href={"#"}
                    on:click|preventDefault={() => share(sellSheet)}>Share</a
                  >
                </span>
              </div>
            </div>
          </div>
        {/each}
        {#if !sellSheets.length && !search}
          <div>
            <div class="grid-row align-center">
              <div class="grid-col c12-12 padding--3 background-white margin-top--smaller">
                <div style="height: 90px;"><Icon type="SellSheets" width="80" height="80" /></div>
                <div class="s3 color-grayDark margin-top--medium padding-bottom--smaller"
                  >Create a Product Sheet to get started!</div
                >
                <div class="s4 color-grayDark"
                  >Choose products from your inventory and share them with your retailers.</div
                >
              </div>
            </div>
          </div>
        {/if}
        {#if !sellSheets.length && search}
          <div>
            <div class="grid-row">
              <div
                class="grid-col c12-12 align-center background-white margin-top--smaller padding-top--medium"
              >
                <div class="s3 color-grayDark padding-bottom--medium"
                  >No Product Sheets found for this search.</div
                >
              </div>
            </div>
          </div>
        {/if}
      </div>
    </div>

    <div class="grid-container">
      <div class="grid-row align-center margin-top--medium margin-bottom--medium">
        {#if sellSheets.length}
          <Pagination {pagination} />
        {/if}
      </div>
    </div>
  </div>

  {#if sheetToCopy}
    <span class="fizz-styles">
      <CopySellSheetModal sellSheet={sheetToCopy} {showCopyModal} {onCloseModal} />
    </span>
  {/if}

  <span class="fizz-styles">
    <ShareSellSheetModal
      {showShareSellSheetModal}
      {shareWithAll}
      {shareWithSelected}
      closeModal={onCloseModal}
      sellSheet={sheetToShare}
      {retailerUserCount}
      {retailersLoaded}
      {searchRetailers}
      showLoading={false}
    />
  </span>
  <span class="fizz-styles">
    <ChooseDistributorModal
      showModal={showChooseDistributorModal}
      closeModal={closeChooseDistributorModal}
      {selectedDistributor}
      {distributors}
      {selectDistributor}
    />
  </span>
</PageWrapper>
